import DomainSourceUpload from "./DomainSourceUpload";
import { useCallback, useEffect, useState } from "react";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, FileDown } from "lucide-react";

const STATUS_STYLE = {
  RECONCILED: "bg-green-100 text-green-800 border-green-300",
  MOVEMENT_RECONCILED: "bg-green-100 text-green-800 border-green-300",
  MATCHED_RECEIPT: "bg-green-100 text-green-800 border-green-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  READY: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_FULL_CYCLE: "bg-green-100 text-green-800 border-green-300",
  COMPOSED_BY_CATEGORY: "bg-green-100 text-green-800 border-green-300",
  COSTING_CONSISTENT: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  PARTIAL: "bg-amber-100 text-amber-800 border-amber-300",
  AMOUNT_DIFFERENCE: "bg-amber-100 text-amber-800 border-amber-300",
  ACCOUNT_MAPPING_DIFFERENCE: "bg-amber-100 text-amber-800 border-amber-300",
  SELLING_PRICE_ONLY: "bg-amber-100 text-amber-800 border-amber-300",
  COSTING_DIFFERENCE: "bg-amber-100 text-amber-800 border-amber-300",
  ANALYSIS_WITHOUT_PROVISION: "bg-amber-100 text-amber-800 border-amber-300",
  PROVISION_WITHOUT_ANALYSIS: "bg-amber-100 text-amber-800 border-amber-300",
  GL_ONLY: "bg-amber-100 text-amber-800 border-amber-300",
  LISTING_ONLY: "bg-amber-100 text-amber-800 border-amber-300",
  AMBIGUOUS: "bg-red-100 text-red-800 border-red-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_PERIOD: "bg-red-100 text-red-800 border-red-300",
  NOT_READY_FOR_FULL_CYCLE: "bg-red-100 text-red-800 border-red-300",
  NOT_APPLICABLE: "bg-slate-100 text-slate-700 border-slate-300",
  SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
};

const NOT_AVAILABLE = "Tidak berlaku / sumber tidak tersedia";
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_SOURCE: "Tertahan — sumber belum tersedia",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun belum disetujui",
  BLOCKED_PERIOD: "Tertahan — tanggal posisi tidak sesuai periode",
  REVIEW_REQUIRED: "Perlu reviu",
};
const LABELS = {
  RECONCILED: "Cocok", MOVEMENT_RECONCILED: "Pergerakan cocok",
  AMOUNT_DIFFERENCE: "Perbedaan nilai",
  ACCOUNT_MAPPING_DIFFERENCE: "Ada kategori belum dipetakan",
  LISTING_ONLY: "Hanya di daftar persediaan", GL_ONLY: "Hanya di buku besar",
  REVIEW_REQUIRED: "Perlu reviu", CLOSING_DIFFERENCE: "Selisih saldo akhir",
  QUANTITY_DIFFERENCE: "Selisih kuantitas", VALUE_DIFFERENCE: "Selisih nilai",
  MISSING_MOVEMENT: "Rincian pergerakan tidak ada",
  NO_MOVEMENT_IN_PERIOD: "Tidak bergerak dalam periode",
  MATCHED_RECEIPT: "Penerimaan tertelusur",
  PURCHASE_WITHOUT_STOCK_RECEIPT: "Pembelian tanpa penerimaan persediaan",
  NOT_AN_INVENTORY_PURCHASE: "Bukan pembelian persediaan",
  DATE_DIFFERENCE: "Perbedaan tanggal",
  COUNT_MATCH: "Sesuai", COUNT_OVER: "Lebih", COUNT_SHORT: "Kurang",
  ACTIVE: "Aktif", SLOW_MOVING: "Bergerak lambat", NO_MOVEMENT: "Tidak bergerak",
  NOT_EXPIRED: "Belum kedaluwarsa", EXPIRING_SOON: "Mendekati kedaluwarsa",
  EXPIRED: "Kedaluwarsa", NOT_APPLICABLE: NOT_AVAILABLE,
  COST_BELOW_NRV: "Biaya di bawah pembanding", COST_ABOVE_NRV: "Biaya di atas pembanding",
  SELLING_PRICE_ONLY: "Hanya perbandingan harga jual",
  NRV_SOURCE_MISSING: "Sumber nilai realisasi neto tidak ada",
  COSTING_CONSISTENT: "Konsisten dengan rata-rata berjalan",
  COSTING_DIFFERENCE: "Ada pengeluaran di luar rata-rata berjalan",
  COSTING_NOT_STATED: "Tidak dapat diukur",
  PROVISION_RECORDED: "Cadangan dicatat",
  PROVISION_WITHOUT_ANALYSIS: "Cadangan tanpa analisis",
  ANALYSIS_WITHOUT_PROVISION: "Analisis tanpa cadangan tercatat",
  COMPOSED_BY_CATEGORY: "Tersusun dari ekspor per kategori",
  SELECTED: "Satu sumber terpilih", AMBIGUOUS: "Perlu pemilihan auditor",
  SOURCE_NOT_SUPPLIED: NOT_AVAILABLE,
  READY: "Siap", PARTIAL: "Sebagian",
  BLOCKED: "Tertahan", READY_FOR_FULL_CYCLE: "Siap untuk audit siklus penuh",
  NOT_READY_FOR_FULL_CYCLE: "Belum siap untuk audit siklus penuh",
  ROLL_FORWARD_SUPPORTED: "Dapat di-roll forward",
  ROLL_BACK_SUPPORTED: "Dapat di-roll back",
  MOVEMENT_GAP: "Pergerakan tidak lengkap", NOT_TESTABLE: "Tidak dapat diuji",
};
const GATE_LABELS = {
  ending_population_exists: "Populasi persediaan akhir ada",
  as_of_date_appropriate: "Tanggal posisi sesuai",
  account_mapping_approved: "Pemetaan akun disetujui",
  tie_out_supportable: "Tie-out ke buku besar dapat diuji",
  quantity_and_value_per_item: "Kuantitas dan nilai per barang tersedia",
  sampling_universe: "Kerangka sampling dapat dibuat",
  jet_universe: "Populasi jurnal dapat dibuat",
  existence_procedure_supportable: "Prosedur keberadaan didukung",
  cutoff_testable: "Cut-off dapat diuji",
  valuation_procedure_supportable: "Prosedur valuasi didukung",
  limitations_disclosed: "Keterbatasan dinyatakan",
  physical_count_complete: "Perhitungan fisik lengkap",
  movement_complete: "Pergerakan lengkap",
  nrv_full: "Nilai realisasi neto penuh",
  expiry_testable: "Kedaluwarsa dapat diuji",
  slow_moving_testable: "Bergerak lambat dapat diuji",
  manufacturing_testable: "Manufaktur dapat diuji",
};
const UNIVERSE_LABELS = {
  ending_balance: "Saldo akhir persediaan", receipts: "Penerimaan", issues: "Pengeluaran",
  count_differences: "Selisih perhitungan fisik", high_value: "Barang bernilai tinggi",
  slow_moving: "Bergerak lambat", nrv_exceptions: "Pengecualian valuasi", cutoff: "Cut-off",
};

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["populasi", "Populasi Persediaan", (v) => v.population?.controls?.items > 0],
  ["pemetaan", "Pemetaan Akun", () => true],
  ["vsgl", "Persediaan vs GL/TB", (v) => !!v.inventory_vs_gl],
  ["pergerakan", "Pergerakan Persediaan", (v) => !!v.movement],
  ["rollforward", "Roll-forward Bulanan", (v) => !!v.monthly_rollforward],
  ["penerimaan", "Penerimaan / GRN", (v) => !!v.receipts],
  ["pengeluaran", "Pengeluaran Barang", (v) => !!v.issues],
  ["opname", "Stock Opname", (v) => !!v.physical_count],
  ["selisih", "Selisih Fisik", (v) => v.physical_count?.items?.length > 0],
  ["cutoff", "Cut-off", (v) => !!v.cutoff || !!v.delivery_cutoff],
  ["slow", "Slow Moving", (v) => !!v.slow_moving],
  ["expired", "Expired", (v) => v.expiry?.status && v.expiry.status !== "NOT_APPLICABLE"],
  ["nrv", "NRV / Valuasi", (v) => !!v.nrv],
  ["anomali", "Negative / Anomaly", (v) => !!v.anomalies],
  ["provisi", "Provision", (v) => !!v.provision],
  ["wip", "WIP / Manufaktur", (v) => v.work_in_progress?.status !== "NOT_APPLICABLE"],
  ["pos", "Pos Rekonsiliasi", (v) => !!v.movement || !!v.inventory_vs_gl],
  ["bukti", "Bukti Audit", () => true],
  ["kesiapan", "Kesiapan Siklus Penuh", (v) => !!v.full_cycle_readiness],
];

// Persediaan — satu posisi audit per tanggal pelaporan, dengan pergerakan, valuasi dan kesiapannya.
export default function Inventory({ id }) {
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ringkasan");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/inventory`).then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/inventory-summary`).then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/inventory/workpaper.xlsx`, `kertas-kerja-persediaan-${id}.xlsx`)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) return <div className="text-xs text-muted-foreground p-4">Memuat data persediaan…</div>;
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="inventory">
      <DomainSourceUpload id={id} endpoint="inventory-sources" label="Unggah sumber persediaan (judul laporan menentukan jenisnya)" accept=".xlsx,.xls,.csv" onUploaded={load} />
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <Boxes size={15} />Persediaan
        </div>
        <div className="text-xs text-muted-foreground">
          {view.audit_period_display}{view.as_of ? ` · posisi per ${view.as_of}` : ""}
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportWorkpaper}
          data-testid="inventory-export">
          <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
        </Button>
      </div>

      {summary && (
        <div className="bg-white border border-border rounded-sm p-2"
          data-testid="inventory-domain-summary">
          <div className="text-xs font-head font-semibold px-1 pb-2">
            Ringkasan Domain Persediaan
            <Badge status={summary.acceptance}
              label={ACCEPTANCE_LABELS[summary.acceptance] || summary.acceptance} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Figure label="Sumber" value={summary.sources} />
            <Figure label="Posisi per" value={summary.as_of} />
            <Figure label="Barang" value={summary.items} />
            <Figure label="Kategori" value={summary.categories} />
            <Figure label="Kuantitas" value={summary.quantity} />
            <Figure label="Nilai persediaan" value={rupiah(summary.value)} />
            <Figure label="Barang tanpa pergerakan" value={summary.items_without_movement} />
            <Figure label="Baris negatif" value={summary.negative_rows} />
            <Figure label="Pemilihan daftar" value={text(summary.listing_selection_status)}
              status={summary.listing_selection_status} />
            <Figure label="Pemetaan akun" value={text(summary.account_mapping_status)}
              status={summary.account_mapping_status} />
            <Figure label="Persediaan ↔ buku besar" value={text(summary.inventory_vs_gl_status)}
              status={summary.inventory_vs_gl_status} />
            <Figure label="Selisih terhadap buku besar"
              value={rupiah(summary.inventory_vs_gl_difference)} />
            <Figure label="Pergerakan" value={text(summary.movement_status)}
              status={summary.movement_status} />
            <Figure label="Roll-forward bulanan" value={text(summary.monthly_rollforward_status)}
              status={summary.monthly_rollforward_status} />
            <Figure label="Penerimaan" value={text(summary.receipts_status)}
              status={summary.receipts_status} />
            <Figure label="Perhitungan fisik" value={text(summary.physical_count_status)}
              status={summary.physical_count_status} />
            <Figure label="Pos cut-off" value={summary.cutoff_items} />
            <Figure label="Bergerak lambat" value={text(summary.slow_moving_status)}
              status={summary.slow_moving_status} />
            <Figure label="Kedaluwarsa" value={text(summary.expiry_status)}
              status={summary.expiry_status} />
            <Figure label="Valuasi" value={text(summary.nrv_status)} status={summary.nrv_status} />
            <Figure label="Metode biaya" value={text(summary.costing_status)}
              status={summary.costing_status} />
            <Figure label="Cadangan" value={text(summary.provision_status)}
              status={summary.provision_status} />
            <Figure label="Barang dalam proses" value={text(summary.wip_status)}
              status={summary.wip_status} />
            <Figure label="Indikator anomali" value={summary.anomaly_findings} />
            <Figure label="Kesiapan siklus penuh" value={text(summary.full_cycle_readiness)}
              status={summary.full_cycle_readiness} />
            <Figure label="Permintaan sumber" value={summary.missing_source_requests} />
          </div>
          {summary.warehouses === 0 && (
            <div className="text-xs text-amber-800 px-1 pt-2" data-testid="inventory-dimension-note">
              Sumber tidak memuat gudang, satuan, nomor batch maupun tanggal kedaluwarsa; pengujian
              per lokasi, per batch dan kedaluwarsa tidak dapat dilakukan dari sumber ini.
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`inventory-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto p-2" data-testid={`inventory-panel-${tab}`}>
          {tab === "ringkasan" && <Sources rows={view.sources} selection={view.listing_selection} />}
          {tab === "populasi" && <Population population={view.population} />}
          {tab === "pemetaan" && <Mapping mapping={view.account_mapping} tie={view.inventory_vs_gl} />}
          {tab === "vsgl" && <TieOut gl={view.inventory_vs_gl} tb={view.inventory_vs_tb} />}
          {tab === "pergerakan" && <Movement result={view.movement} />}
          {tab === "rollforward" && <RollForward result={view.monthly_rollforward} />}
          {tab === "penerimaan" && <Receipts result={view.receipts} />}
          {tab === "pengeluaran" && <Issues result={view.issues} />}
          {tab === "opname" && <Count result={view.physical_count} roll={view.count_roll_forward} />}
          {tab === "selisih" && <Count result={view.physical_count} roll={view.count_roll_forward} />}
          {tab === "cutoff" && <Cutoff result={view.cutoff} windows={view.cutoff_windows}
            delivery={view.delivery_cutoff} />}
          {tab === "slow" && <SlowMoving result={view.slow_moving} />}
          {tab === "expired" && <Expiry result={view.expiry} />}
          {tab === "nrv" && <Valuation result={view.nrv} costing={view.costing} />}
          {tab === "anomali" && <Anomalies result={view.anomalies} />}
          {tab === "provisi" && <Provision result={view.provision} population={view.population} />}
          {tab === "wip" && <Wip result={view.work_in_progress} />}
          {tab === "pos" && <Open view={view} />}
          {tab === "bukti" && <Evidence rows={view.evidence} />}
          {tab === "kesiapan" && <Readiness readiness={view.full_cycle_readiness}
            universes={view.sampling_universes} journals={view.journal_universe}
            requests={view.missing_sources} />}
        </div>
      </div>
    </div>
  );
}

function text(status) {
  return LABELS[status] || status || "—";
}

function Badge({ status, label }) {
  return (
    <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}`}>
      {label || text(status)}
    </span>
  );
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Empty({ id }) {
  return <div className="text-xs text-muted-foreground p-2" data-testid={id}>{NOT_AVAILABLE}</div>;
}

function Sources({ rows = [], selection }) {
  return (
    <div className="space-y-3 text-xs">
      <table className="w-full audit-table text-xs" data-testid="inventory-sources">
        <thead><tr><th>Berkas</th><th>Jenis sumber</th><th>Kategori</th><th className="text-right">Baris</th>
          <th>Periode</th><th>Dicetak</th><th>Catatan sumber</th></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id}>
            <td>{row.filename}</td><td>{row.source_type}</td><td>{row.category}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
            <td>{row.declared_range?.stated}</td><td>{row.printed_at?.date}</td>
            <td className="max-w-md">{(row.notes || []).join(" ")}</td>
          </tr>))}
        </tbody>
      </table>
      {selection && (
        <div data-testid="inventory-selection">
          <span className="font-medium">Pemilihan daftar persediaan</span>
          <Badge status={selection.status} />
          <div className="text-muted-foreground mt-1">{selection.basis}</div>
        </div>
      )}
    </div>
  );
}

function Population({ population }) {
  const controls = population?.controls || {};
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-population">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kategori</th><th className="text-right">Barang</th>
          <th className="text-right">Kuantitas</th><th className="text-right">Nilai</th></tr></thead>
        <tbody>{Object.entries(controls.by_category || {}).map(([name, entry]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{entry.items}</td>
            <td className="text-right tabular-nums">{entry.quantity}</td>
            <td className="text-right tabular-nums">{rupiah(entry.value)}</td></tr>))}
        </tbody>
      </table>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Figure label="Baris" value={controls.rows} />
        <Figure label="Gudang diketahui" value={controls.warehouses} />
        <Figure label="Batch diketahui" value={controls.batches} />
        <Figure label="Duplikat barang-lokasi" value={controls.duplicate_item_locations} />
        <Figure label="Nilai saldo awal" value={rupiah(controls.opening_value)} />
        <Figure label="Nilai masuk" value={rupiah(controls.value_in)} />
        <Figure label="Nilai keluar" value={rupiah(controls.value_out)} />
        <Figure label="Nilai akhir" value={rupiah(controls.value)} />
      </div>
      <div className="text-muted-foreground">{population?.basis}</div>
    </div>
  );
}

function Mapping({ mapping, tie }) {
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-mapping">
      <div>Status pemetaan<Badge status={mapping?.status} /></div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kategori</th><th>Akun</th></tr></thead>
        <tbody>{Object.entries(mapping?.category_accounts || {}).map(([category, account]) => (
          <tr key={category}><td>{category}</td><td>{account}</td></tr>))}
        </tbody>
      </table>
      {!!(tie?.unmapped_categories || []).length && (
        <div className="text-amber-800" data-testid="inventory-unmapped">
          Kategori belum dipetakan: {tie.unmapped_categories.join(", ")} — nilai{" "}
          {rupiah(tie.unmapped_value)}. Nilai ini tidak dialokasikan ke akun mana pun.
        </div>
      )}
      <div className="text-muted-foreground">
        Golongan umur: {(mapping?.ageing_buckets || []).join(" / ")} hari · ambang mendekati
        kedaluwarsa {mapping?.expiring_soon_days} hari. Keduanya dapat diubah auditor.
      </div>
    </div>
  );
}

function TieOut({ gl, tb }) {
  if (!gl) return <Empty id="inventory-vsgl-empty" />;
  const table = (result, key) => (
    <table className="w-full audit-table text-xs" data-testid={`inventory-${key}`}>
      <thead><tr><th>Akun</th><th>Kategori</th><th className="text-right">Nilai daftar</th>
        <th className="text-right">Saldo</th><th className="text-right">Selisih</th>
        <th>Status</th><th>Simpulan auditor</th></tr></thead>
      <tbody>{(result.items || []).map((row, index) => (
        <tr key={index}>
          <td>{row.account_code}</td><td>{(row.categories || []).join(", ")}</td>
          <td className="text-right tabular-nums">{rupiah(row.listing_value)}</td>
          <td className="text-right tabular-nums">{rupiah(row.gl_balance)}</td>
          <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
          <td>{text(row.status)}</td>
          <td className="text-muted-foreground">Belum disimpulkan</td>
        </tr>))}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-3 text-xs">
      <div className="font-medium">Persediaan ↔ buku besar</div>
      {table(gl, "vsgl")}
      <div className="text-muted-foreground">{gl.basis}</div>
      {tb && (
        <>
          <div className="font-medium">Persediaan ↔ neraca saldo</div>
          {table(tb, "vstb")}
          <div className="text-amber-800" data-testid="inventory-tb-note">{tb.basis}</div>
        </>
      )}
    </div>
  );
}

function Movement({ result }) {
  if (!result) return <Empty id="inventory-movement-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-movement">
      <div>Cakupan {result.summary?.coverage}<Badge status={result.summary?.status} /></div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kode barang</th><th>Kategori</th><th className="text-right">Kts. awal</th>
          <th className="text-right">Masuk</th><th className="text-right">Keluar</th>
          <th className="text-right">Kts. akhir</th><th className="text-right">Dihitung</th>
          <th className="text-right">Selisih</th><th className="text-right">Baris</th>
          <th>Status</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
          <tr key={index}>
            <td>{row.item_code}</td><td>{row.category}</td>
            <td className="text-right tabular-nums">{row.opening_quantity}</td>
            <td className="text-right tabular-nums">{row.quantity_in}</td>
            <td className="text-right tabular-nums">{row.quantity_out}</td>
            <td className="text-right tabular-nums">{row.closing_quantity}</td>
            <td className="text-right tabular-nums">{row.computed_closing_quantity}</td>
            <td className="text-right tabular-nums">{row.quantity_difference}</td>
            <td className="text-right tabular-nums">{row.movement_rows}</td>
            <td>{text(row.status)}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function RollForward({ result }) {
  if (!result) return <Empty id="inventory-rollforward-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-rollforward">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Masa</th><th className="text-right">Kts. awal</th>
          <th className="text-right">Nilai awal</th><th className="text-right">Masuk</th>
          <th className="text-right">Keluar</th><th className="text-right">Kts. akhir</th>
          <th className="text-right">Nilai akhir</th><th>Status</th>
          <th>Simpulan auditor</th></tr></thead>
        <tbody>{(result.months || []).map((row) => (
          <tr key={row.period}>
            <td>{row.period}</td>
            <td className="text-right tabular-nums">{row.opening_quantity}</td>
            <td className="text-right tabular-nums">{rupiah(row.opening_value)}</td>
            <td className="text-right tabular-nums">{row.quantity_in}</td>
            <td className="text-right tabular-nums">{row.quantity_out}</td>
            <td className="text-right tabular-nums">{row.closing_quantity}</td>
            <td className="text-right tabular-nums">{rupiah(row.closing_value)}</td>
            <td>{text(row.status)}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Receipts({ result }) {
  if (!result) return <Empty id="inventory-receipts-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-receipts">
      <div>Cakupan {result.summary?.coverage}<Badge status={result.summary?.status} /></div>
      <div className="text-muted-foreground">
        {result.purchase_lines_bridged_by_receiving_history} baris pembelian tertelusur melalui
        histori penerimaan barang. {result.stock_receipts_without_purchase} penerimaan dari pemasok
        belum tertaut ke pembelian; penerimaan dari produksi dan pemindahan tidak dihitung di sini.
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Pemasok</th><th>No. dokumen</th><th>Tanggal</th><th>Barang</th>
          <th className="text-right">Kuantitas</th><th className="text-right">Kts. pergerakan</th>
          <th>Status</th><th>Bukti</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
          <tr key={index}>
            <td>{row.vendor}</td><td>{row.document_number}</td><td>{row.document_date}</td>
            <td>{row.item_description}</td>
            <td className="text-right tabular-nums">{row.quantity}</td>
            <td className="text-right tabular-nums">{row.movement_quantity ?? "—"}</td>
            <td>{text(row.status)}</td><td>{(row.evidence || []).join(", ")}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Issues({ result }) {
  if (!result) return <Empty id="inventory-issues-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-issues">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Klasifikasi pengeluaran</th><th className="text-right">Baris</th>
          <th className="text-right">Kuantitas</th><th className="text-right">Nilai</th></tr></thead>
        <tbody>{Object.entries(result.by_class || {}).map(([name, entry]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{entry.rows}</td>
            <td className="text-right tabular-nums">{entry.quantity}</td>
            <td className="text-right tabular-nums">{rupiah(entry.value)}</td></tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Count({ result, roll }) {
  if (!result) return <Empty id="inventory-count-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-count">
      <div className="text-amber-800" data-testid="inventory-count-basis">{result.basis}</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kode barang</th><th>Deskripsi</th><th>Tanggal</th><th>Referensi</th>
          <th>Alasan</th><th className="text-right">Kts. buku</th>
          <th className="text-right">Kts. hitung</th><th className="text-right">Selisih</th>
          <th>Status</th><th>Simpulan auditor</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
          <tr key={index}>
            <td>{row.item_code}</td><td>{row.item_description}</td><td>{row.count_date}</td>
            <td>{row.adjustment_reference}</td><td>{row.reason}</td>
            <td className="text-right">{row.book_quantity ?? "—"}</td>
            <td className="text-right">{row.count_quantity ?? "—"}</td>
            <td className="text-right tabular-nums">{row.difference}</td>
            <td>{text(row.status)}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      {roll && (
        <div data-testid="inventory-count-roll">
          Roll-forward ke tanggal pelaporan<Badge status={roll.status} />
          <div className="text-muted-foreground mt-1">{roll.basis}</div>
        </div>
      )}
    </div>
  );
}

function Cutoff({ result, windows, delivery }) {
  return (
    <div className="space-y-3 text-xs" data-testid="inventory-cutoff">
      {result ? (
        <>
          <div className="font-medium">Pergerakan persediaan di sekitar tanggal pelaporan</div>
          <div>
            {Object.entries(windows || {}).map(([window, entry]) => (
              <span key={window} className="mr-3">±{window} hari: {entry.count}</span>))}
          </div>
          <table className="w-full audit-table text-xs">
            <thead><tr><th>Kode barang</th><th>Tanggal</th><th>Uraian</th><th>Referensi</th>
              <th>Klasifikasi</th><th>Simpulan auditor</th></tr></thead>
            <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
              <tr key={index}>
                <td>{row.item_code}</td><td>{row.movement_date}</td><td>{row.narrative}</td>
                <td>{row.reference}</td><td>{text(row.classification || row.status)}</td>
                <td className="text-muted-foreground">Belum disimpulkan</td>
              </tr>))}
            </tbody>
          </table>
        </>
      ) : <Empty id="inventory-cutoff-empty" />}
      {delivery && (
        <div data-testid="inventory-delivery-cutoff">
          <div className="font-medium">Pengiriman di sekitar tanggal pelaporan</div>
          <div>
            {Object.entries(delivery.windows || {}).map(([window, entry]) => (
              <span key={window} className="mr-3">±{window} hari: {entry.count}</span>))}
          </div>
          <div className="text-muted-foreground mt-1">{delivery.basis}</div>
        </div>
      )}
    </div>
  );
}

function SlowMoving({ result }) {
  if (!result) return <Empty id="inventory-slow-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-slow">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Golongan umur</th><th className="text-right">Barang</th>
          <th className="text-right">Nilai</th></tr></thead>
        <tbody>{Object.entries(result.by_bucket || {}).map(([name, entry]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{entry.items}</td>
            <td className="text-right tabular-nums">{rupiah(entry.value)}</td></tr>))}
        </tbody>
      </table>
      <div className="text-amber-800" data-testid="inventory-slow-basis">{result.basis}</div>
    </div>
  );
}

function Expiry({ result }) {
  if (!result || result.status === "NOT_APPLICABLE") {
    return (
      <div className="text-xs p-2" data-testid="inventory-expiry-na">
        <div>{NOT_AVAILABLE}</div>
        <div className="text-muted-foreground mt-1">{result?.basis}</div>
      </div>
    );
  }
  return (
    <table className="w-full audit-table text-xs" data-testid="inventory-expiry">
      <thead><tr><th>Kode barang</th><th>Kedaluwarsa</th><th className="text-right">Sisa hari</th>
        <th className="text-right">Nilai</th><th>Status</th></tr></thead>
      <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
        <tr key={index}>
          <td>{row.item_code}</td><td>{row.expiry_date}</td>
          <td className="text-right tabular-nums">{row.days_remaining}</td>
          <td className="text-right tabular-nums">{rupiah(row.value)}</td>
          <td>{text(row.status)}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Valuation({ result, costing }) {
  if (!result) return <Empty id="inventory-nrv-empty" />;
  return (
    <div className="space-y-3 text-xs" data-testid="inventory-nrv">
      <div>Ukuran<Badge status={result.status} /></div>
      <div className="text-amber-800" data-testid="inventory-nrv-basis">{result.basis}</div>
      <div>
        {result.items_compared} barang dapat dibandingkan; {result.items_without_a_selling_price}{" "}
        barang tidak memiliki harga jual pada periode ini.
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kode barang</th><th className="text-right">Kuantitas</th>
          <th className="text-right">Biaya/satuan</th><th className="text-right">Harga jual/satuan</th>
          <th className="text-right">Pembanding</th><th className="text-right">Eksposur</th>
          <th>Ukuran</th><th>Status</th><th>Simpulan auditor</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 300).map((row, index) => (
          <tr key={index}>
            <td>{row.item_code}</td>
            <td className="text-right tabular-nums">{row.quantity}</td>
            <td className="text-right tabular-nums">{rupiah(row.unit_cost)}</td>
            <td className="text-right tabular-nums">{rupiah(row.selling_price)}</td>
            <td className="text-right tabular-nums">{rupiah(row.comparison_value)}</td>
            <td className="text-right tabular-nums">{rupiah(row.exposure)}</td>
            <td>{text(row.measure)}</td><td>{text(row.status)}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      {costing && (
        <div data-testid="inventory-costing">
          <div className="font-medium">Metode biaya<Badge status={costing.status} /></div>
          <div>
            Dinyatakan sumber: {costing.basis_stated_by_source || "tidak dinyatakan"} · terindikasi
            dari pergerakan: {costing.basis_indicated_by_movement || "tidak dapat disimpulkan"} ·{" "}
            {costing.issues_agreeing_with_running_average} dari {costing.issues_checked} pengeluaran
            sesuai rata-rata berjalan.
          </div>
          <div className="text-muted-foreground mt-1">{costing.note}</div>
        </div>
      )}
    </div>
  );
}

function Anomalies({ result }) {
  if (!result) return <Empty id="inventory-anomalies-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-anomalies">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Indikator</th><th className="text-right">Jumlah</th></tr></thead>
        <tbody>{Object.entries(result.findings || {}).map(([name, count]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <div className="text-amber-800" data-testid="inventory-anomalies-note">{result.note}</div>
    </div>
  );
}

function Provision({ result, population }) {
  if (!result) return <Empty id="inventory-provision-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-provision">
      <div>Status<Badge status={result.status} /></div>
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Persediaan bruto</td>
            <td className="text-right tabular-nums">{rupiah(population?.controls?.value)}</td></tr>
          <tr><td>Cadangan penurunan nilai</td>
            <td className="text-right tabular-nums">{rupiah(result.provision)}</td></tr>
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Wip({ result }) {
  if (!result || result.status === "NOT_APPLICABLE") return <Empty id="inventory-wip-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="inventory-wip">
      <div>Status<Badge status={result.status} /></div>
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Saldo buku besar</td>
            <td className="text-right tabular-nums">{rupiah(result.ledger_value)}</td></tr>
          <tr><td>Nilai daftar persediaan</td>
            <td className="text-right tabular-nums">{rupiah(result.listing_value)}</td></tr>
          <tr><td>Selisih</td>
            <td className="text-right tabular-nums">{rupiah(result.difference)}</td></tr>
        </tbody>
      </table>
      <div className="text-amber-800">{result.basis}</div>
    </div>
  );
}

function Open({ view }) {
  const rows = [];
  (view.movement?.items || []).forEach((row) => {
    if (!["MOVEMENT_RECONCILED", "NO_MOVEMENT_IN_PERIOD"].includes(row.status))
      rows.push({ source: "Pergerakan", key: row.item_code, status: row.status,
        difference: row.quantity_difference });
  });
  (view.inventory_vs_gl?.items || []).forEach((row) => {
    if (row.status !== "RECONCILED")
      rows.push({ source: "Persediaan ↔ GL", key: row.account_code, status: row.status,
        difference: row.difference });
  });
  (view.receipts?.items || []).forEach((row) => {
    if (!["MATCHED_RECEIPT", "NOT_AN_INVENTORY_PURCHASE"].includes(row.status))
      rows.push({ source: "Penerimaan", key: row.document_number, status: row.status,
        difference: row.quantity_difference });
  });
  return (
    <table className="w-full audit-table text-xs" data-testid="inventory-open">
      <thead><tr><th>Rekonsiliasi</th><th>Kode</th><th>Status</th><th className="text-right">Selisih</th>
        <th>Simpulan auditor</th></tr></thead>
      <tbody>{rows.slice(0, 500).map((row, index) => (
        <tr key={index}>
          <td>{row.source}</td><td>{row.key}</td><td>{text(row.status)}</td>
          <td className="text-right tabular-nums">{row.difference ?? "—"}</td>
          <td className="text-muted-foreground">Belum disimpulkan</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Evidence({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="inventory-evidence">
      <thead><tr><th>Berkas</th><th>Jenis</th><th>SHA-256</th><th>Periode</th><th>Dicetak</th>
        <th>Diperoleh</th></tr></thead>
      <tbody>{rows.map((row, index) => (
        <tr key={index}>
          <td>{row.filename}</td><td>{row.source_type}</td>
          <td className="font-mono">{(row.sha256 || "").slice(0, 16)}…</td>
          <td>{row.period}</td><td>{row.printed_at || "—"}</td><td>{row.obtained_at}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Readiness({ readiness, universes, journals, requests }) {
  if (!readiness) return <Empty id="inventory-readiness-empty" />;
  const row = ([name, entry]) => (
    <tr key={name}>
      <td>{GATE_LABELS[name] || name}</td>
      <td><span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[entry.status] || ""}`}>
        {text(entry.status)}</span></td>
      <td className="max-w-xl">{entry.basis}</td>
    </tr>);
  return (
    <div className="space-y-3 text-xs" data-testid="inventory-readiness">
      <div className="font-medium">
        <span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[readiness.status] || ""}`}
          data-testid="inventory-readiness-status">{text(readiness.status)}</span>
      </div>
      <div className="text-muted-foreground">{readiness.basis}</div>
      <table className="w-full audit-table text-xs" data-testid="inventory-gates">
        <thead><tr><th>Gerbang</th><th>Status</th><th>Dasar</th></tr></thead>
        <tbody>{Object.entries(readiness.gates || {}).map(row)}</tbody>
      </table>
      <div className="font-medium">Kemampuan prosedur (keterbatasan, bukan kegagalan siklus)</div>
      <table className="w-full audit-table text-xs" data-testid="inventory-capabilities">
        <thead><tr><th>Kemampuan</th><th>Status</th><th>Dasar</th></tr></thead>
        <tbody>{Object.entries(readiness.capabilities || {}).map(row)}</tbody>
      </table>
      {universes && (
        <>
          <div className="font-medium">Kerangka sampling (belum ada sampel yang dipilih)</div>
          <table className="w-full audit-table text-xs" data-testid="inventory-universes">
            <thead><tr><th>Kerangka</th><th className="text-right">Jumlah</th>
              <th className="text-right">Nilai</th><th>Catatan</th></tr></thead>
            <tbody>{Object.entries(universes.universes || {}).map(([name, entry]) => (
              <tr key={name}><td>{UNIVERSE_LABELS[name] || name}</td>
                <td className="text-right tabular-nums">{entry.count}</td>
                <td className="text-right tabular-nums">{rupiah(entry.value)}</td>
                <td>{entry.note}</td></tr>))}
            </tbody>
          </table>
          <div className="text-muted-foreground">{universes.basis}</div>
        </>
      )}
      {journals && (
        <div data-testid="inventory-jet">
          Populasi jurnal siklus persediaan: {journals.rows} baris pada {journals.journals} jurnal,
          akun {(journals.accounts || []).join(", ")}. {journals.note}
        </div>
      )}
      {!!(requests || []).length && (
        <>
          <div className="font-medium">Permintaan sumber yang belum tersedia</div>
          <table className="w-full audit-table text-xs" data-testid="inventory-missing-sources">
            <thead><tr><th>Sumber</th><th>Field yang diperlukan</th><th>Alasan</th></tr></thead>
            <tbody>{requests.map((request, index) => (
              <tr key={index}>
                <td>{request.source}</td>
                <td className="max-w-md">{(request.required_fields || []).join(", ")}</td>
                <td className="max-w-md">{request.why}</td>
              </tr>))}
            </tbody>
          </table>
        </>)}
    </div>
  );
}
